/**
 * CW-SF-R2 TKT-6 — Pipeline Coverage Ratio (CEO role).
 *
 * Open pipeline this fiscal Q / quarterly target. Color-coded scalar:
 * green ≥3x, yellow 2-3x, red <2x. Empty-state CTA when no quota.
 */
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface ApiResponse extends SfWidgetEnvelope {
  quota_missing: boolean;
  q_label: string | null;
  fiscal_year?: number;
  fiscal_quarter?: number;
  pipeline: number;
  count?: number;
  target: number | null;
  coverage_ratio: number | null;
  source?: string | null;
  derived_from_annual?: boolean;
}

const REFRESH_S = 5 * 60;

function ratioClass(ratio: number | null): string {
  if (ratio === null) return '';
  if (ratio >= 3) return 'sf-coverage--green';
  if (ratio >= 2) return 'sf-coverage--yellow';
  return 'sf-coverage--red';
}

export function SfPipelineCoverageWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/pipeline-coverage', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  if (data?.quota_missing) {
    return (
      <div className="sf-widget sf-widget--coverage">
        <SfWidgetShell
          envelope={data}
          loading={loading}
          error={error}
          hasItems={true}
          emptyMessage=""
        >
          <div className="sf-coverage sf-coverage--empty">
            <div className="sf-coverage__empty-title">No quarterly quota set</div>
            <div className="sf-coverage__empty-body">
              Open pipeline this Q: {formatCurrency(data.pipeline)} ({data.count ?? 0} opps)
            </div>
            <RouterLink to="/admin/sf/quotas" className="sf-coverage__empty-cta">
              Set quota in admin →
            </RouterLink>
          </div>
        </SfWidgetShell>
      </div>
    );
  }

  const ratio = data?.coverage_ratio ?? null;
  const cls = ratioClass(ratio);

  return (
    <div className={`sf-widget sf-widget--coverage ${cls}`}>
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={true}
        emptyMessage="—"
      >
        <div className="sf-coverage">
          <div className="sf-coverage__header">
            <span className="sf-coverage__label">{data?.q_label ?? '—'} coverage</span>
            <span className="sf-coverage__ratio">
              {ratio !== null ? `${ratio}x` : '—'}
            </span>
          </div>
          <div className="sf-coverage__numbers">
            <span>{formatCurrency(data?.pipeline ?? 0)}</span>
            <span className="sf-coverage__sep">/</span>
            <span>{formatCurrency(data?.target ?? 0)}</span>
          </div>
          {data?.derived_from_annual && (
            <div className="sf-coverage__note">Target derived from annual ÷ 4</div>
          )}
        </div>
      </SfWidgetShell>
    </div>
  );
}
